import React from 'react'
import { TileWrapper } from '@vp/tile-wrapper'
import { createOnClickTrackingHandler } from '../../utils/trackingUtils'
import { getNewTileTrackingConfig } from '../../utils/trackingConfig'
import { useLogger } from '@vp/ubik-context'
import { FEATURE_TILE_SCHEMA } from '../../utils/trackingSchemas'
import type { Tile as TileData } from '../../types/homepageFeatureTileRow'
import type { TileSkin } from '../../types/sharedTypes'

const getTrackingDataNewTile = (tile: TileData, pageTrackingZone: string, position: number) => {
  const tileHeader = tile.template?.tileDetails?.header?.value ?? ''
  const destinationUrl = tile.template?.tileDetails?.link?.href ?? ''

  const trackingData = {
    tileHeader,
    destinationUrl,
    position,
    pageZone: pageTrackingZone,
    productKey: tile.template?.productKey ?? '',
    mpvId: tile.template?.productsList?.selectedProduct?.mpvId,
    sourcePagePath: typeof window !== 'undefined' ? window.location.pathname : '/',
  }
  return trackingData
}

interface TileProps {
  data: TileData
  position: number
  loadType: string
  tileSkin?: TileSkin
  trackingPageZone: string
  enableCampaignCallouts: boolean
  /** Optional so an omitted prop is treated as "no price", matching the `!!showPrice` coercion below. */
  showPrice?: boolean
  locale: string
  marginBottom?: number
}

const Tile = (props: TileProps) => {
  const { data, position, tileSkin, trackingPageZone, enableCampaignCallouts, showPrice, locale } = props
  const { template } = data

  const logger = useLogger()

  if (!template) {
    logger.warn('Home Page Tile component: Missing template', { data })
    return null
  }

  const trackingMethod = (event: React.MouseEvent) => {
    const trackingData = getTrackingDataNewTile(data, trackingPageZone, position)
    if (!trackingData) return

    const trackingAndNavigation = createOnClickTrackingHandler(
      getNewTileTrackingConfig(trackingData),
      logger,
      FEATURE_TILE_SCHEMA,
      data.template?.tileDetails?.link?.href,
    )
    if (trackingAndNavigation) {
      trackingAndNavigation(event)
    }
  }

  return (
    <TileWrapper
      tileProps={template}
      locale={locale}
      clickHandler={trackingMethod}
      // Swan's StandardTile only supports 'standard' | 'product' | 'circular' | null for skin --
      // 'gallery' (a valid TileSkin value elsewhere in this codebase) isn't one of its options.
      // If 'gallery' is ever actually passed here, swan falls back to its own default rather than
      // throwing, so this cast doesn't change behavior versus what already happens at runtime.
      customTileProps={{ skin: tileSkin as 'standard' | 'product' | 'circular' | null | undefined }}
      extraProps={{
        enableCampaignCallouts,
        showPrice: !!showPrice,
        enableCouponCallouts: !!showPrice,
        shouldHideAutomaticTags: true,
        primaryLinkConfig: { skin: 'unstyled' },
      }}
      hideDescription={!template.tileDetails?.description?.key /* this indicates a user provided description */}
    />
  )
}

export default Tile
